<template>
  <div>
    <PageAllow controller="ReturnOrders" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherDatetime
              v-model="options.created_time"
              remove-icon
              type="date"
              :label="$t('ReturnOrder.Fields.created_time')"
            >
            </SearcherDatetime>
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              small
              color="warning"
              class="ms-1 float-end"
              @click="batchConfirmDialog = true"
            >
              <v-icon x-small class="pe-1">fa-align-justify</v-icon>
              {{ $t("ReturnOrder.batchConfirm") }}
            </v-btn>
            <v-btn
              small
              dark
              color="print"
              class="ms-1 float-end"
              @click="exportDialog = true"
            >
              <v-icon x-small class="pe-1">fa-file-export</v-icon>
              {{ $t("ReturnOrder.export") }}
            </v-btn>
            <v-btn
              small
              dark
              color="print"
              class="ms-1 float-end"
              @click="importDialog = true"
            >
              <v-icon x-small class="pe-1">fa-file-import</v-icon>
              {{ $t("ReturnOrder.import") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="account_id"
                  v-model="accountIdKeyword"
                  :label="$t('ReturnOrder.Fields.account_id')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="orderCodeKeyword"
                  :label="$t('ReturnOrder.Fields.order_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="shop"
                  v-model="shopKeyword"
                  :label="$t('ReturnOrder.Fields.shop_code') + ' / ' + $t('ReturnOrder.Fields.shop_name')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="memo"
                  v-model="memoKeyword"
                  :label="$t('ReturnOrder.Fields.memo')"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="returnOrderItems"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.check_price="{ item }">
            <span class="text-no-wrap">
              {{ $t("Global.money") }}{{ item.check_price | formatMoneyFloor }}
            </span>
          </template>
          <template v-slot:item.shop_code="{ item }">
            <span class="text-no-wrap">
              {{ item.shop_name }}
            </span>
            <br />
            <span class="text-no-wrap caption secondary--text">
              {{ item.shop_code }}
            </span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | returnOrderStatusColor"
              :text-color="item.status | returnOrderStatusTextColor"
            >{{ item.status | returnOrderStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <OrderActions
              :order="item"
              showView
              showEdit
              @done="getOrdersFromAPI()"
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <DialogExport
      v-model="exportDialog"
    />
    <ImportExcelAlert
      v-model="importDialog"
      :objName="$t('ReturnOrder.title')"
      downloadTemplateUrl="download/template/ent_return_order_templat.xls"
      @import="
        excelFile = $event;
        importExcel();
      "
    />

    <DialogImportResult
      v-model="importResultDialog"
      :importResultErr="importResultErr"
      :reportUrl="importResult.report_url ? importResult.report_url : ''"
      @cancel="importResultDialog = false;"
    />

    <!-- 批量确认 -->
    <DialogAlert
      v-model="batchConfirmDialog"
      :title="$t('$vuetify.warning')"
      :message="$t('ReturnOrder.messages.batchConfirmAlert')"
      persistent
      useConfirm
      @confirm="
        batchConfirmDialog = false;
        batchConfirm();
      "
    />

    <BatchConfirmAlert
      v-model="batchConfirmInfoDialog"
      :title="$t('$vuetify.alert')"
      :message="batchConfirmInfo"
      persistent
      :closable="errorCount > 0"
      @cancel="batchConfirmStopFlg = true"
    />

  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import ReturnOrders from "@/api/return_orders";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherDatetime from "@/components/plugins/SearcherDatetime";
import ImportExcelAlert from "@/components/plugins/ImportExcelAlert";
import DialogAlert from "@/components/plugins/DialogAlert";
import DialogExport from "./plugins/DialogExport";
import DialogImportResult from "./plugins/DialogImportResult";
import OrderActions from "./plugins/Actions"
import BatchConfirmAlert from "./plugins/BatchConfirmAlert";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time: new Date(new Date().getTime()).toISOString().substr(0, 10),
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherDatetime,
    ImportExcelAlert,
    DialogAlert,
    DialogExport,
    DialogImportResult,
    OrderActions,
    BatchConfirmAlert,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.returnOrderManagement"),
          disabled: false,
          replace: true,
          href: "/return-orders",
        },
        {
          text: i18n.t("Nav.returnOrders"),
          disabled: true,
          href: "/return-orders",
        }
      ];
    },
    headers() {
      return [
        {
          text: i18n.t("ReturnOrder.Fields.account_id"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "account_id",
        },
        {
          text:
            i18n.t("ReturnOrder.Fields.order_code") +
            " / " +
            i18n.t("ReturnOrder.Fields.created_time"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "order_code",
        },
        {
          text: i18n.t("ReturnOrder.Fields.shop"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "shop_code",
        },
        {
          text: i18n.t("ReturnOrder.Fields.check_price"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "check_price",
        },
        {
          text: i18n.t("ReturnOrder.Fields.return_type"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "return_type_str",
        },
        {
          text: i18n.t("ReturnOrder.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("ReturnOrder.Fields.memo"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "memo",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },
    returnOrderItems() {
      //前端搜索
      return this.returnOrders.filter(
        (item) =>
          ((this.accountIdKeyword && (item.account_id.indexOf(this.accountIdKeyword) > -1)) || !this.accountIdKeyword) &&
          ((this.orderCodeKeyword && (item.order_code.indexOf(this.orderCodeKeyword) > -1)) || !this.orderCodeKeyword) &&
          ((this.shopKeyword && (item.shop_code.indexOf(this.shopKeyword) > -1 || item.shop_name.indexOf(this.shopKeyword) > -1)) || !this.shopKeyword) &&
          ((this.memoKeyword && item.memo && (item.memo.indexOf(this.memoKeyword) > -1)) || !this.memoKeyword)
      );
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions("return-orders", [])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();
        }.bind(this)
      );
  },

  data: () => ({
    options: JSON.parse(
      sessionStorage.getItem("return-orders-options")
        ? sessionStorage.getItem("return-orders-options")
        : JSON.stringify(defaultOptions)
    ),
    returnOrders: [],
    accountIdKeyword: '',
    shopKeyword: '',
    orderCodeKeyword: '',
    memoKeyword: '',
    exportDialog: false,
    importDialog: false,
    excelFile: null,
    importResultDialog: false,
    importResultErr: [],
    importResult: [],
    batchConfirmDialog: false,
    batchConfirmData: [],
    batchConfirmInfoDialog: false,
    batchConfirmInfo: i18n.t("ReturnOrder.messages.batchConfirmInfo", [0, 0]),
    batchConfirmErrorDialog: false,
    batchConfirmErrorData: [],
    batchConfirmStopFlg: false,
    successCount: 0,
    errorCount: 0,
    alreadyConfirmedOrderLength: 0,
    error_order_id: [],
  }),

  watch: {
    $route: {
      handler() {
        this.$commonUtils
          .loadOptions("return-orders", []).then(
          function (data) {
            if (data.options) {
              this.options = data.options;
            } else this.resetSearch();
          }.bind(this)
        );
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getOrdersFromAPI();
        sessionStorage.setItem("return-orders-options",JSON.stringify(this.options));
      },
    },
    importResultDialog: {
      deep: true,
      handler() {
        if (this.importResultDialog == false) {
          this.getOrdersFromAPI();
        }
      },
    }
  },

  methods: {
    resetSearch() {
      this.returnOrders = [];
      this.accountIdKeyword = '';
      this.shopKeyword = '';
      this.orderCodeKeyword = '';
      this.memoKeyword = '';
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [
        this.$refs.account_id,
        this.$refs.shop,
        this.$refs.order_code,
        this.$refs.memo,
      ].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options);
    },
    getOrdersFromAPI() {
      let options = {
        created_time_from: this.options.created_time + ' 00:00:00',
        created_time_to: this.options.created_time + ' 23:59:59',
      }
      ReturnOrders.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.returnOrders = response.data.return_orders;
          }
        }.bind(this)
      );
    },
    importExcel() {
      this.$store.dispatch("fullscreenLoading");
      ReturnOrders.imports(this.excelFile)
        .then(
          function (response) {
            if (response.data.status.isOK()) {
              if (response.data.err_data && response.data.err_data.length) {
                this.importDialog = false;
                this.importResultDialog = true;
                this.importResultErr = response.data.err_data;
                this.importResult = response.data.report_download_record;
              } else {
                this.$snackbar.info(i18n.t("Import.success", [i18n.t("ReturnOrder.title")]));
                this.importDialog = false;
                this.getOrdersFromAPI();
              }
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        )
        .catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
    },
    batchConfirm(){
      if (this.returnOrderItems.length > 0 ) {
        this.batchConfirmInfoDialog = true;
        this.batchConfirmStopFlg = false;
        this.batchConfirmData = JSON.parse(JSON.stringify(this.returnOrderItems));
        this.confirm(this.batchConfirmData[0], 0)
      }
    },
    confirm(data, index) {
      if (index <= this.batchConfirmData.length - 1 && !this.batchConfirmStopFlg) {
        ReturnOrders.batchConfirm({
          account_id: data.account_id,
          id: data.id
        }).then(
            function (response) {
              if (response.data.status.isOK()) {
                if (response.data.confirm_flg) {
                  ++this.successCount;
                } else {
                  this.error_order_id.push(response.data.order_id);
                  ++this.errorCount;
                }
              }
              else {
                if (response.data.error.code != "ERR333") {
                  this.error_order_id.push(response.data.order_id);
                  ++this.errorCount;
                  if (response.data.error.code == "ERR668") {
                    this.batchConfirmErrorData.push(response.data.data[0]);
                    this.batchConfirmErrorDialog = true;
                  }
                } else {
                  this.getOrdersFromAPI();
                  this.error_order_id = [];
                  this.successCount = 0;
                  this.errorCount = 0;
                  this.alreadyConfirmedOrderLength = 0;
                  this.batchConfirmInfoDialog = false;
                  this.batchConfirmData = [];
                }
              }
              //实时计数
              this.batchConfirmInfo = i18n.t("ReturnOrder.messages.batchConfirmInfo", [
                this.successCount,
                this.errorCount,
              ]);
              ++this.alreadyConfirmedOrderLength; //计已确认的单子总数
              if (response.data.status.isOK() || response.data.error.code != "ERR333"
              ) {
                //回调
                this.confirm(this.batchConfirmData[(index + 1)], (index + 1))
              }
            }.bind(this)
        );
      } else {
        if (index > this.batchConfirmData.length - 1) {
          this.$snackbar.success(i18n.t("ReturnOrder.messages.batchConfirmSuccess"));
        }
        this.getOrdersFromAPI();
        this.error_order_id = [];
        this.successCount = 0;
        this.errorCount = 0;
        this.alreadyConfirmedOrderLength = 0;
        this.batchConfirmInfoDialog = false;
        this.batchConfirmData = [];
      }
    }
  },
};
</script>
